/**
 * Billing Service
 * HTTP service for subscription and billing management
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  Subscription,
  UsageResponse,
  PricingPlan,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  CreateCheckoutSessionRequest,
  CheckoutSessionResponse,
  CustomerPortalResponse,
  ListPricingPlansResponse,
  SubscriptionCreatedResponse,
  CancelSubscriptionResponse
} from '../models/billing.models';

export interface CreditBalance {
  organization_id: string;
  subscription_id: string;
  plan_tier: string;
  credits_allocated: number;
  credits_used: number;
  credits_remaining: number;
  credits_purchased_remaining?: number;
  credits_carried_over: number;
  credits_frozen: boolean;
  current_period_start: Date;
  current_period_end: Date;
  credits_expire_at: Date | null;
  carryover_enabled: boolean;
  reset_policy: string;
  is_low?: boolean;
  is_exhausted?: boolean;
  has_paid_plan?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/billing`;

  /**
   * Get all available pricing plans
   */
  getPricingPlans(): Observable<ListPricingPlansResponse> {
    return this.http.get<ListPricingPlansResponse>(`${this.apiUrl}/plans`);
  }

  /**
   * Get current subscription for the organization
   */
  getSubscription(): Observable<Subscription> {
    return this.http.get<Subscription>(`${this.apiUrl}/subscription`);
  }

  /**
   * Create a new subscription
   */
  createSubscription(request: CreateSubscriptionRequest): Observable<SubscriptionCreatedResponse> {
    return this.http.post<SubscriptionCreatedResponse>(`${this.apiUrl}/subscribe`, request);
  }

  /**
   * Update subscription (change plan or cancel at period end)
   */
  updateSubscription(request: UpdateSubscriptionRequest): Observable<Subscription> {
    return this.http.put<Subscription>(`${this.apiUrl}/subscription`, request);
  }

  /**
   * Cancel subscription
   * @param immediately - If true, cancel immediately. Otherwise, cancel at period end.
   */
  cancelSubscription(immediately: boolean = false): Observable<CancelSubscriptionResponse> {
    const params = new HttpParams().set('immediately', immediately.toString());
    return this.http.post<CancelSubscriptionResponse>(`${this.apiUrl}/cancel`, {}, { params });
  }

  /**
   * Get usage statistics for current billing period
   */
  getUsage(): Observable<UsageResponse> {
    return this.http.get<UsageResponse>(`${this.apiUrl}/usage`);
  }

  /**
   * Create Stripe Checkout session
   * Returns a URL to redirect the user to Stripe Checkout
   */
  createCheckoutSession(request: CreateCheckoutSessionRequest): Observable<CheckoutSessionResponse> {
    return this.http.post<CheckoutSessionResponse>(`${this.apiUrl}/checkout`, request);
  }

  /**
   * Get Stripe Customer Portal URL
   * Returns a URL to redirect the user to manage their subscription
   */
  getCustomerPortalUrl(returnUrl: string): Observable<CustomerPortalResponse> {
    const params = new HttpParams().set('return_url', returnUrl);
    return this.http.get<CustomerPortalResponse>(`${this.apiUrl}/portal`, { params });
  }

  /**
   * Upgrade subscription to a new plan
   */
  upgradePlan(stripePriceId: string): Observable<Subscription> {
    return this.updateSubscription({ stripe_price_id: stripePriceId });
  }

  /**
   * Downgrade subscription to a new plan
   */
  downgradePlan(stripePriceId: string): Observable<Subscription> {
    return this.updateSubscription({ stripe_price_id: stripePriceId });
  }

  /**
   * Reactivate a canceled subscription
   */
  reactivateSubscription(): Observable<Subscription> {
    return this.updateSubscription({ cancel_at_period_end: false });
  }

  /**
   * Schedule subscription cancellation at period end
   */
  scheduleCancellation(): Observable<Subscription> {
    return this.updateSubscription({ cancel_at_period_end: true });
  }

  /**
   * Start a free trial
   */
  startTrial(stripePriceId: string, trialDays: number = 14): Observable<SubscriptionCreatedResponse> {
    return this.createSubscription({
      stripe_price_id: stripePriceId,
      trial_days: trialDays
    });
  }

  /**
   * Redirect to Stripe Checkout
   */
  redirectToCheckout(stripePriceId: string, trialDays?: number): Observable<void> {
    const request: CreateCheckoutSessionRequest = {
      stripe_price_id: stripePriceId,
      success_url: `${window.location.origin}/billing/success?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${window.location.origin}/billing`,
      trial_days: trialDays
    };

    return new Observable(observer => {
      this.createCheckoutSession(request).subscribe({
        next: (response) => {
          window.location.href = response.url;
          observer.next();
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  /**
   * Redirect to Stripe Customer Portal
   */
  redirectToCustomerPortal(): Observable<void> {
    const returnUrl = `${window.location.origin}/billing`;

    return new Observable(observer => {
      this.getCustomerPortalUrl(returnUrl).subscribe({
        next: (response) => {
          window.location.href = response.url;
          observer.next();
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  // ============================================================================
  // Phase 2: Credit Purchase Methods
  // ============================================================================

  /**
   * Get Stripe publishable key for client-side payments
   */
  getStripeConfig(): Observable<{ publishable_key: string; mock_mode: boolean }> {
    return this.http.get<{ publishable_key: string; mock_mode: boolean }>(
      `${this.apiUrl}/stripe-config`
    );
  }

  /**
   * Get available credit packages
   */
  getCreditPackages(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/credits/packages`);
  }

  /**
   * Create credit purchase
   */
  createCreditPurchase(packageId: string, paymentMethodId?: string): Observable<any> {
    const payload: any = {
      package_id: packageId
    };

    // Only include payment_method_id if provided
    if (paymentMethodId) {
      payload.payment_method_id = paymentMethodId;
    }

    return this.http.post<any>(`${this.apiUrl}/credits/purchase`, payload);
  }

  /**
   * Get credit purchase history
   */
  getCreditPurchaseHistory(limit: number = 10, skip: number = 0): Observable<any> {
    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('skip', skip.toString());

    return this.http.get<any>(`${this.apiUrl}/credits/purchases/history`, { params });
  }

  getPaymentHistory(limit: number = 50): Observable<any> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.get<any>(`${this.apiUrl}/payment-history`, { params });
  }

  downloadCreditPurchaseInvoice(purchaseId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/credits/purchases/${purchaseId}/invoice`, {
      responseType: 'blob'
    });
  }

  /**
   * Get credit balance
   */
  getCreditBalance(): Observable<CreditBalance> {
    const headers = new HttpHeaders().set('X-Skip-Error-Notification', 'true');
    return this.http.get<CreditBalance>(`${this.apiUrl}/credits/balance`, { headers });
  }

  confirmCreditPurchase(paymentIntentId: string): Observable<{ status: string; credits_purchased: number; credits_remaining?: number }> {
    return this.http.post<{ status: string; credits_purchased: number; credits_remaining?: number }>(
      `${this.apiUrl}/credits/confirm`,
      { payment_intent_id: paymentIntentId }
    );
  }

  confirmCheckoutSession(sessionId: string): Observable<{ status: string; session_id: string; credits_allocated?: number }> {
    return this.http.get<{ status: string; session_id: string; credits_allocated?: number }>(
      `${this.apiUrl}/checkout/confirm`,
      { params: { session_id: sessionId } }
    );
  }

  // ============================================================================
  // Phase 3: Usage Analytics Methods
  // ============================================================================

  /**
   * Get usage statistics
   */
  getUsageStatistics(period: 'day' | 'week' | 'month' = 'month'): Observable<any> {
    const params = new HttpParams().set('period', period);
    return this.http.get<any>(`${environment.apiUrl}/analytics/credits/usage`, { params });
  }

  /**
   * Get usage forecast
   */
  getUsageForecast(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/analytics/credits/forecast`);
  }

  /**
   * Get usage trends
   */
  getUsageTrends(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/analytics/credits/trends`);
  }
}

